import React from 'react';
import { Document, Page, Text, View, StyleSheet, Font, Link, Image } from '@react-pdf/renderer';
import type { LetterheadConfig, TypographyStyle } from '../contexts/DocumentContext';
import MarkdownIt from 'markdown-it';

export interface ThemeColors {
  primary: string;
  secondary: string;
  textPrimary: string;
  textSecondary: string;
  background: string;
  divider: string;
}

// Register fonts (regular weight only - we use color/size for emphasis instead of font weights)
Font.register({
  family: 'Roboto',
  src: 'https://fonts.gstatic.com/s/roboto/v30/KFOmCnqEu92Fr1Mu4mxP.ttf',
});

Font.register({
  family: 'Open Sans',
  src: 'https://fonts.gstatic.com/s/opensans/v34/memSYaGs126MiZpBA-UvWbX2vVnXBbObj2OVZyOOSr4dVJWUgsjZ0C4nY1M2xLER.ttf',
});

Font.register({
  family: 'Lato',
  src: 'https://fonts.gstatic.com/s/lato/v24/S6uyw4BMUTPHjx4wWw.ttf',
});

interface PDFDocumentProps {
  content: string;
  typographyStyle: TypographyStyle;
  letterheadConfig: LetterheadConfig;
  themeColors: ThemeColors;
  logoPng?: string; // PNG data URL for logo (converted from SVG)
}

// Map browser font families to registered PDF fonts
const mapFontFamily = (fontFamily: string): string => {
  const lowerFont = fontFamily.toLowerCase();

  // Check if it's a system font stack or contains multiple fonts
  if (lowerFont.includes('-apple-system') || lowerFont.includes('system-ui') || lowerFont.includes('segoe ui')) {
    return 'Roboto'; // Default to Roboto for system fonts
  }

  // Map specific fonts to registered fonts
  if (lowerFont.includes('roboto')) return 'Roboto';
  if (lowerFont.includes('open sans')) return 'Open Sans';
  if (lowerFont.includes('lato')) return 'Lato';

  // Default fallback
  return 'Roboto';
};

// Create styles
const createStyles = (typography: TypographyStyle, theme: ThemeColors) =>
  StyleSheet.create({
    page: {
      flexDirection: 'column',
      backgroundColor: '#ffffff',
      paddingTop: 80,
      paddingBottom: 50,
      paddingHorizontal: 72, // 1 inch = 72 points
    },
    pageWithLetterhead: {
      paddingTop: 120, // Extra space for letterhead header
      paddingBottom: 90, // Extra space for letterhead footer
    },
    header: {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      height: 80,
      backgroundColor: theme.background,
      flexDirection: 'row',
      justifyContent: 'flex-start',
      alignItems: 'center',
      paddingHorizontal: 40,
      borderBottomWidth: 2,
      borderBottomColor: theme.primary,
    },
    headerLogo: {
      width: 32,
      height: 32,
      marginRight: 12,
    },
    headerText: {
      flexDirection: 'column',
    },
    headerTitle: {
      fontSize: 16,
      color: theme.textPrimary,
      marginBottom: 2,
    },
    headerSubtitle: {
      fontSize: 10,
      color: theme.textSecondary,
    },
    footer: {
      position: 'absolute',
      bottom: 0,
      left: 0,
      right: 0,
      height: 50,
      backgroundColor: theme.background,
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      paddingHorizontal: 40,
      borderTopWidth: 2,
      borderTopColor: theme.divider,
    },
    footerText: {
      fontSize: 9,
      color: theme.textSecondary,
    },
    content: {
      fontFamily: mapFontFamily(typography.fontFamily),
      fontSize: typography.fontSize,
      lineHeight: typography.lineHeight,
      color: '#000000',
    },
    paragraph: {
      marginBottom: typography.paragraphSpacing * typography.fontSize,
    },
    heading1: {
      fontSize: typography.fontSize * typography.headingScale * 2,
      marginTop: typography.sectionSpacing * typography.fontSize,
      marginBottom: 24,
      lineHeight: 1.3, // Better spacing for wrapped lines
    },
    heading2: {
      fontSize: typography.fontSize * typography.headingScale * 1.5,
      marginTop: typography.sectionSpacing * 0.8 * typography.fontSize,
      marginBottom: 20,
      lineHeight: 1.3, // Better spacing for wrapped lines
    },
    heading3: {
      fontSize: typography.fontSize * typography.headingScale * 1.25,
      marginTop: typography.headingTopMargin * typography.fontSize,
      marginBottom: 16,
      lineHeight: 1.3, // Better spacing for wrapped lines
    },
    listItem: {
      flexDirection: 'row',
      marginBottom: 4,
    },
    bullet: {
      width: 20,
    },
    listContent: {
      flex: 1,
    },
  });

// Markdown parser using markdown-it for PDF
const md = new MarkdownIt();

// Remove or replace emojis with text equivalents
const sanitizeEmojis = (text: string): string => {
  // Common emoji replacements
  const emojiReplacements: { [key: string]: string } = {
    '📧': '',
    '📞': '',
    '🌐': '',
    '❤️': '',
    '✓': 'X',
    '✅': 'X',
    '❌': '',
    '⚠️': '!',
  };

  let result = text;
  Object.entries(emojiReplacements).forEach(([emoji, replacement]) => {
    result = result.replace(new RegExp(emoji, 'g'), replacement);
  });

  // Remove any remaining emojis (Unicode emoji ranges)
  result = result.replace(
    /[\u{1F600}-\u{1F64F}]|[\u{1F300}-\u{1F5FF}]|[\u{1F680}-\u{1F6FF}]|[\u{1F1E0}-\u{1F1FF}]|[\u{2600}-\u{26FF}]|[\u{2700}-\u{27BF}]|[\u{1F900}-\u{1F9FF}]|[\u{1FA00}-\u{1FA6F}]/gu,
    ''
  );

  return result;
};

const parseInlineContent = (content: string, styles: any): React.ReactNode[] => {
  // Sanitize emojis first
  content = sanitizeEmojis(content);
  const nodes: React.ReactNode[] = [];

  // Simple regex-based inline parsing - using color and size instead of font weights
  const boldItalicRegex = /(\*\*\*|___|~~|\*\*|__|\*|_|`|\[.*?\]\(.*?\))/g;
  const parts = content.split(boldItalicRegex);

  parts.forEach((part, i) => {
    if (!part) return;

    // Handle formatting markers
    if (part === '***' || part === '___') {
      // Bold + Italic - use darker color and slightly larger size
      const nextPart = parts[i + 1];
      if (nextPart) {
        nodes.push(<Text key={i} style={{ color: '#000000', fontSize: styles.content.fontSize * 1.05 }}>{nextPart}</Text>);
        parts[i + 1] = ''; // Skip next part
      }
    } else if (part === '**' || part === '__') {
      // Bold - use darker color
      const nextPart = parts[i + 1];
      if (nextPart) {
        nodes.push(<Text key={i} style={{ color: '#000000' }}>{nextPart}</Text>);
        parts[i + 1] = '';
      }
    } else if (part === '*' || part === '_') {
      // Italic - use slightly darker gray
      const nextPart = parts[i + 1];
      if (nextPart) {
        nodes.push(<Text key={i} style={{ color: '#1a1a1a' }}>{nextPart}</Text>);
        parts[i + 1] = '';
      }
    } else if (part === '~~') {
      // Strikethrough
      const nextPart = parts[i + 1];
      if (nextPart) {
        nodes.push(<Text key={i} style={{ textDecoration: 'line-through', color: '#666666' }}>{nextPart}</Text>);
        parts[i + 1] = '';
      }
    } else if (part === '`') {
      // Inline code
      const nextPart = parts[i + 1];
      if (nextPart) {
        nodes.push(<Text key={i} style={{ fontFamily: 'Courier', backgroundColor: '#f5f5f5', padding: 2, fontSize: styles.content.fontSize * 0.9 }}>{nextPart}</Text>);
        parts[i + 1] = '';
      }
    } else if (part.match(/^\[.*?\]\(.*?\)$/)) {
      // Link [text](url)
      const match = part.match(/^\[(.*?)\]\((.*?)\)$/);
      if (match) {
        nodes.push(<Link key={i} src={match[2]} style={{ color: '#1976d2', textDecoration: 'underline' }}>{match[1]}</Link>);
      }
    } else if (!['***', '___', '**', '__', '*', '_', '~~', '`'].includes(part)) {
      // Regular text
      nodes.push(part);
    }
  });

  return nodes.length > 0 ? nodes : [content];
};

const parseMarkdown = (markdown: string, styles: any) => {
  const elements: React.ReactElement[] = [];
  let key = 0;

  const lines = markdown.split('\n');
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];
    const trimmed = line.trim();

    // Headings
    if (trimmed.startsWith('# ')) {
      const content = trimmed.substring(2);
      elements.push(
        <Text key={`h1-${key++}`} style={{ ...styles.heading1, marginBottom: 30 }}>
          {parseInlineContent(content, styles)}
        </Text>
      );
      i++;
    } else if (trimmed.startsWith('## ')) {
      const content = trimmed.substring(3);
      elements.push(
        <Text key={`h2-${key++}`} style={{ ...styles.heading2, marginBottom: 24 }}>
          {parseInlineContent(content, styles)}
        </Text>
      );
      i++;
    } else if (trimmed.startsWith('### ')) {
      const content = trimmed.substring(4);
      elements.push(
        <Text key={`h3-${key++}`} style={{ ...styles.heading3, marginBottom: 18 }}>
          {parseInlineContent(content, styles)}
        </Text>
      );
      i++;
    } else if (trimmed.startsWith('#### ')) {
      const content = trimmed.substring(5);
      elements.push(
        <Text key={`h4-${key++}`} style={{ ...styles.heading3, fontSize: styles.heading3.fontSize * 0.9, marginBottom: 14 }}>
          {parseInlineContent(content, styles)}
        </Text>
      );
      i++;
    }
    // Horizontal rule
    else if (trimmed === '---' || trimmed === '***' || trimmed === '___') {
      elements.push(
        <View key={`hr-${key++}`} style={{ borderBottomWidth: 1, borderBottomColor: '#e0e0e0', marginVertical: 12 }} />
      );
      i++;
    }
    // Blockquote
    else if (trimmed.startsWith('> ')) {
      const quoteLines = [];
      while (i < lines.length && lines[i].trim().startsWith('> ')) {
        quoteLines.push(lines[i].trim().substring(2));
        i++;
      }
      elements.push(
        <View key={`quote-${key++}`} style={{ borderLeftWidth: 4, borderLeftColor: '#1976d2', paddingLeft: 12, marginVertical: 8, backgroundColor: '#f5f5f5', padding: 8 }}>
          <Text style={{ color: '#333333', fontSize: styles.content.fontSize * 0.95 }}>
            {quoteLines.map((line, idx) => parseInlineContent(line, styles))}
          </Text>
        </View>
      );
    }
    // Code block
    else if (trimmed.startsWith('```')) {
      const codeLines = [];
      i++; // Skip opening ```
      while (i < lines.length && !lines[i].trim().startsWith('```')) {
        codeLines.push(lines[i]);
        i++;
      }
      i++; // Skip closing ```
      elements.push(
        <View key={`code-${key++}`} style={{ backgroundColor: '#2d2d2d', padding: 12, marginVertical: 8, borderRadius: 4 }}>
          <Text style={{ fontFamily: 'Courier', fontSize: styles.content.fontSize * 0.85, color: '#f8f8f2' }}>
            {codeLines.join('\n')}
          </Text>
        </View>
      );
    }
    // Unordered list
    else if (trimmed.match(/^[-*+]\s+/)) {
      const listItems = [];
      while (i < lines.length && lines[i].trim().match(/^[-*+]\s+/)) {
        const content = lines[i].trim().substring(2);
        listItems.push(
          <View key={`li-${key++}`} style={styles.listItem}>
            <Text style={styles.bullet}>•</Text>
            <Text style={styles.listContent}>{parseInlineContent(content, styles)}</Text>
          </View>
        );
        i++;
      }
      elements.push(
        <View key={`list-${key++}`} style={{ marginBottom: 8 }}>
          {listItems}
        </View>
      );
    }
    // Ordered list
    else if (trimmed.match(/^\d+\.\s+/)) {
      const listItems = [];
      let num = 1;
      while (i < lines.length && lines[i].trim().match(/^\d+\.\s+/)) {
        const content = lines[i].trim().replace(/^\d+\.\s+/, '');
        listItems.push(
          <View key={`oli-${key++}`} style={styles.listItem}>
            <Text style={styles.bullet}>{num}.</Text>
            <Text style={styles.listContent}>{parseInlineContent(content, styles)}</Text>
          </View>
        );
        num++;
        i++;
      }
      elements.push(
        <View key={`olist-${key++}`} style={{ marginBottom: 8 }}>
          {listItems}
        </View>
      );
    }
    // Table (basic support)
    else if (trimmed.startsWith('|')) {
      const tableLines = [];
      while (i < lines.length && lines[i].trim().startsWith('|')) {
        tableLines.push(lines[i].trim());
        i++;
      }

      if (tableLines.length > 0) {
        const rows = tableLines.filter((_, idx) => idx !== 1); // Skip separator line
        elements.push(
          <View key={`table-${key++}`} style={{ marginVertical: 8, borderWidth: 1, borderColor: '#e0e0e0' }}>
            {rows.map((row, rowIdx) => {
              const cells = row.split('|').filter(c => c.trim()).map(c => c.trim());
              return (
                <View key={`row-${rowIdx}`} style={{ flexDirection: 'row', borderBottomWidth: rowIdx === 0 ? 2 : 1, borderBottomColor: '#e0e0e0' }}>
                  {cells.map((cell, cellIdx) => (
                    <View key={`cell-${cellIdx}`} style={{ flex: 1, padding: 6, borderRightWidth: cellIdx < cells.length - 1 ? 1 : 0, borderRightColor: '#e0e0e0' }}>
                      <Text style={{ fontSize: styles.content.fontSize * 0.9, color: rowIdx === 0 ? '#000000' : '#333333' }}>
                        {parseInlineContent(cell, styles)}
                      </Text>
                    </View>
                  ))}
                </View>
              );
            })}
          </View>
        );
      }
    }
    // Empty line
    else if (trimmed === '') {
      i++;
    }
    // Paragraph
    else if (trimmed) {
      const paragraphLines = [];
      while (i < lines.length && lines[i].trim() && !lines[i].trim().match(/^(#{1,6}\s|[-*+]\s|\d+\.\s|>\s|```|\||---)/)) {
        paragraphLines.push(lines[i].trim());
        i++;
      }
      if (paragraphLines.length > 0) {
        elements.push(
          <Text key={`p-${key++}`} style={styles.paragraph}>
            {parseInlineContent(paragraphLines.join(' '), styles)}
          </Text>
        );
      }
    } else {
      i++;
    }
  }

  return elements;
};

export const PDFDocument: React.FC<PDFDocumentProps> = ({
  content,
  typographyStyle,
  letterheadConfig,
  themeColors,
  logoPng,
}) => {
  const styles = createStyles(typographyStyle, themeColors);
  const contentElements = parseMarkdown(content, styles);

  return (
    <Document>
      <Page
        size="A4"
        style={letterheadConfig.enabled ? [styles.page, styles.pageWithLetterhead] : styles.page}
      >
        {/* Header (letterhead) - fixed on every page */}
        {letterheadConfig.enabled && (
          <View style={styles.header} fixed>
            {logoPng && <Image src={logoPng} style={styles.headerLogo} />}
            <View style={styles.headerText}>
              <Text style={styles.headerTitle}>{letterheadConfig.logoText || letterheadConfig.companyName}</Text>
              {letterheadConfig.tagline && (
                <Text style={styles.headerSubtitle}>{letterheadConfig.tagline}</Text>
              )}
            </View>
          </View>
        )}

        {/* Content */}
        <View style={styles.content}>{contentElements}</View>

        {/* Footer (letterhead) - fixed on every page */}
        {letterheadConfig.enabled && (
          <View style={styles.footer} fixed>
            <Text style={styles.footerText}>
              {[
                letterheadConfig.phone,
                letterheadConfig.email,
                letterheadConfig.website,
              ]
                .filter(Boolean)
                .join(' • ')}
            </Text>
          </View>
        )}
      </Page>
    </Document>
  );
};
